import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Popover, Typography } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { isTruthy } from "helpers/methods";
import { FileField, RequestFile, RequestParticipant } from "models/interfaces";
import moment from "moment";
import { useRef, useState } from "react";
import { fieldColorFor } from "screens/SignRequest/SignRequestHelper";

const CustomDateFieldElement = ({
  field,
  dispatch,
  selectedFile,
  coords,
  disableFields,
  currentReviewer,
}: {
  field: FileField;
  dispatch: Function;
  selectedFile: RequestFile;
  coords?: any;
  disableFields?: boolean;
  currentReviewer?: RequestParticipant;
}) => {
  const format = field.dateFormat || "MM/DD/YYYY";
  const display = field.value || format;
  const isPlaceholder = !field.value;
  const styleSize = field.styles?.size ?? 16;
  const widthBasedSize =
    coords?.pageWidth && coords.pageWidth > 0
      ? (coords.pageWidth - 24) / 6
      : styleSize;
  const heightBasedSize = coords?.pageHeight
    ? coords.pageHeight - 4
    : styleSize;
  const fontSize = Math.max(
    8,
    Math.min(styleSize, widthBasedSize, heightBasedSize),
  );
  const iconSize = Math.max(14, Math.min(fontSize * 1.25, 20));

  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  const handleSelect = (newValue: Date | null) => {
    setOpen(false);
    if (!newValue || !moment(newValue).isValid()) return;
    const formatted = moment(newValue).format(format);
    dispatch({
      type: "UPDATE_FIELD",
      fileId: selectedFile.unsignedPdf,
      fieldId: field.name,
      value: formatted,
    });
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    dispatch({
      type: "UPDATE_FIELD",
      fileId: selectedFile.unsignedPdf,
      fieldId: field.name,
      value: "",
    });
  };

  const signerColor = fieldColorFor(field.signerIndex);
  const isReviewer = isTruthy(currentReviewer?.email);
  const backgroundColor = !disableFields
    ? `${signerColor}17`
    : isReviewer
      ? `${signerColor}17`
      : field.required
        ? "rgb(252, 224, 224)"
        : "transparent";
  const borderStyle = !disableFields
    ? `1px dashed ${signerColor}`
    : isReviewer
      ? `1px solid ${signerColor}`
      : "1px solid red";

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        gap: "2px",
        px: "2px",
        boxSizing: "border-box",
        backgroundColor,
        border: borderStyle,
      }}
    >
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize,
          fontFamily: field.styles?.fontFamily,
          color: isPlaceholder ? "#9CA3AF" : (field.styles?.color ?? "#1A1A1A"),
          fontWeight: field.styles?.fontWeight?.bold ? "bold" : "normal",
          fontStyle: field.styles?.fontWeight?.italic ? "italic" : "normal",
          textDecoration: field.styles?.fontWeight?.underline
            ? "underline"
            : "none",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          userSelect: "none",
          lineHeight: 1,
        }}
      >
        {display}
      </Typography>
      {!!field.value && disableFields && !field.readOnly && (
        <IconButton
          size="small"
          onClick={handleClear}
          title="Clear date"
          aria-label="Clear date"
          sx={{ flex: "0 0 auto", padding: 0, color: "#646464" }}
        >
          <CloseIcon sx={{ fontSize: iconSize }} />
        </IconButton>
      )}
      <IconButton
        ref={anchorRef}
        size="small"
        disabled={!disableFields || field.readOnly}
        aria-label={`Choose date${field.value ? `, current value ${field.value}` : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          if (!disableFields || field.readOnly) return;
          setOpen(true);
        }}
        sx={{ flex: "0 0 auto", padding: 0, color: "#646464" }}
      >
        <CalendarMonthIcon sx={{ fontSize: iconSize }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateCalendar
            value={
              field.value && moment(field.value, format).isValid()
                ? moment(field.value, format).toDate()
                : null
            }
            onChange={handleSelect}
          />
        </LocalizationProvider>
      </Popover>
    </Box>
  );
};

export default CustomDateFieldElement;
